/**
 * Sub-component (not a routed page): ground-truth editor for submission verification
 * training data (SubmissionVerificationExtractedData). Edits title, description,
 * price, image URLs, and the isProductPage/available checkboxes, nulling or
 * defaulting dependent fields as those flags toggle, plus a copy-JSON button.
 * State is owned by TrainingDataCreateDisplay and mutated via the use-immer Updater
 * prop; composes InputField, CheckboxField, and ImageUrlsField. No API calls.
 */
import React from "react";
import InputField, { FieldConfig } from "@/components/InputField";
import { SubmissionVerificationExtractedData } from "@/types/trainingData";
import { Updater } from "use-immer";
import CheckboxField from "@/components/CheckboxField";
import ImageUrlsField from "@/components/ImageUrlsField";

interface SubmissionVerificationExtractedDataProps {
    submissionVerificationExtractedData: SubmissionVerificationExtractedData | null;
    setSubmissionVerificationExtractedData: Updater<SubmissionVerificationExtractedData | null>;
    readonly?: boolean;
    label?: string;
    setLabel?: (label: string) => void;
}

const SubmissionVerificationExtractedDataForm: React.FC<
    SubmissionVerificationExtractedDataProps
> = ({
    submissionVerificationExtractedData,
    setSubmissionVerificationExtractedData,
    readonly = false,
    label,
    setLabel,
}) => {
    if (!submissionVerificationExtractedData) {
        return null; // Don't render if the ground truth object isn't initialized yet
    }

    const gtTitleFieldConfig: FieldConfig = {
        key: "gtTitle",
        label: "Title",
        type: "text",
        readonly: readonly || !submissionVerificationExtractedData.isProductPage,
        fieldState: submissionVerificationExtractedData.title || "",
        setFieldState: (value: string) => {
            setSubmissionVerificationExtractedData((draft) => {
                if (draft) draft.title = value;
            });
        },
        placeholder: submissionVerificationExtractedData.isProductPage
            ? "enter product title"
            : "N/A (not a product page)",
    };

    const gtDescriptionFieldConfig: FieldConfig = {
        key: "gtDescription",
        label: "Description",
        type: "textarea",
        readonly: readonly || !submissionVerificationExtractedData.isProductPage,
        fieldState: submissionVerificationExtractedData.description || "",
        setFieldState: (value: string) => {
            setSubmissionVerificationExtractedData((draft) => {
                if (draft) draft.description = value;
            });
        },
        placeholder: submissionVerificationExtractedData.isProductPage
            ? "enter product description"
            : "N/A (not a product page)",
    };

    const gtPriceFieldConfig: FieldConfig = {
        key: "gtPrice",
        label: "Price",
        type: "number",
        readonly: readonly || !submissionVerificationExtractedData.isProductPage,
        fieldState: submissionVerificationExtractedData.price,
        setFieldState: (value: string | null) => {
            setSubmissionVerificationExtractedData((draft) => {
                if (draft) draft.price = value;
            });
        },
        min: 0,
        max: 999999,
        useTwoDecimalPlaces: true,
        placeholder: !submissionVerificationExtractedData.isProductPage
            ? "N/A (not a product page)"
            : undefined,
    };

    const addImageUrlField = () => {
        if (readonly || !submissionVerificationExtractedData.isProductPage) return;

        const imageUrls = submissionVerificationExtractedData.imageUrls || [];
        if (imageUrls.length < 20) {
            setSubmissionVerificationExtractedData((draft) => {
                if (draft) {
                    if (!draft.imageUrls) draft.imageUrls = [];
                    draft.imageUrls.push("");
                }
            });
        }
    };

    const updateImageUrl = (index: number, value: string) => {
        if (readonly || !submissionVerificationExtractedData.isProductPage) return;

        setSubmissionVerificationExtractedData((draft) => {
            if (draft && draft.imageUrls) draft.imageUrls[index] = value;
        });
    };

    const removeImageUrl = (index: number) => {
        if (readonly || !submissionVerificationExtractedData.isProductPage) return;

        setSubmissionVerificationExtractedData((draft) => {
            if (draft && draft.imageUrls) {
                const newImageUrls = draft.imageUrls.filter((_, i) => i !== index);
                draft.imageUrls = newImageUrls.length === 0 ? [""] : newImageUrls;
            }
        });
    };

    return (
        <div className="w-full h-full flex flex-col">
            <h3 className="text-sm font-medium mb-2 tracking-widest">Ground Truth</h3>
            <div className="flex-1 overflow-y-auto pr-2">
                <div className="space-y-4">
                    <InputField fieldConfig={gtTitleFieldConfig} />
                    <InputField fieldConfig={gtDescriptionFieldConfig} height="h-96" />
                    <InputField fieldConfig={gtPriceFieldConfig} />

                    <div className="flex flex-row space-x-4">
                        <CheckboxField
                            label="is product page"
                            checked={submissionVerificationExtractedData.isProductPage}
                            onChange={(checked) =>
                                setSubmissionVerificationExtractedData((draft) => {
                                    if (draft) {
                                        draft.isProductPage = checked;
                                        // If unchecking isProductPage, set all fields to null
                                        if (!checked) {
                                            draft.title = null;
                                            draft.description = null;
                                            draft.price = null;
                                            draft.available = null;
                                            draft.imageUrls = null;
                                        } else {
                                            // If checking isProductPage, initialize fields to default values
                                            draft.title = draft.title ?? "";
                                            draft.description = draft.description ?? "";
                                            draft.price = draft.price ?? null;
                                            draft.available = draft.available ?? false;
                                            draft.imageUrls = draft.imageUrls ?? [""];
                                        }
                                    }
                                })
                            }
                            disabled={readonly}
                        />
                        {submissionVerificationExtractedData.isProductPage && (
                            <CheckboxField
                                label="available"
                                checked={submissionVerificationExtractedData.available ?? false}
                                onChange={(checked) =>
                                    setSubmissionVerificationExtractedData((draft) => {
                                        if (draft) {
                                            draft.available = checked;
                                            // If unchecking available, set price to null
                                            if (!checked) {
                                                draft.price = null;
                                            }
                                            // Update label suffix
                                            if (setLabel && label !== undefined) {
                                                if (checked) {
                                                    // Add "-available" suffix if not already present
                                                    if (!label.endsWith("-available")) {
                                                        setLabel(label + "-available");
                                                    }
                                                } else {
                                                    // Remove "-available" suffix if present
                                                    if (label.endsWith("-available")) {
                                                        setLabel(label.slice(0, -10));
                                                    }
                                                }
                                            }
                                        }
                                    })
                                }
                                disabled={readonly}
                            />
                        )}
                    </div>

                    {submissionVerificationExtractedData.isProductPage && (
                        <ImageUrlsField
                            imageUrls={submissionVerificationExtractedData.imageUrls || [""]}
                            onUpdateImageUrl={updateImageUrl}
                            onAddImageUrl={addImageUrlField}
                            onRemoveImageUrl={removeImageUrl}
                            readonly={readonly}
                            maxUrls={20}
                        />
                    )}

                    <button
                        onClick={() => {
                            const jsonString = JSON.stringify(
                                submissionVerificationExtractedData,
                                null,
                                2
                            );
                            navigator.clipboard.writeText(jsonString).then(
                                () => {
                                    alert("Extracted data JSON copied to clipboard!");
                                },
                                (err) => {
                                    console.error("Failed to copy JSON:", err);
                                    alert("Failed to copy JSON to clipboard");
                                }
                            );
                        }}
                        className="w-full mt-4 px-4 py-2 bg-black hover:bg-gray-800 text-white font-medium rounded-md transition-colors"
                    >
                        Copy Extracted Data JSON
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SubmissionVerificationExtractedDataForm;
